import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions } from '@ngrx/effects';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { select, Store } from '@ngrx/store';
import { selectZefDialogState, zefDialogClose, ZefDialogModule } from '@zerops/zef/dialog';
import { ZefScrollbarComponent } from '@zerops/zef/scrollbar';
import {
  Subject,
  filter,
  map,
  switchMap,
  withLatestFrom,
  tap,
  distinctUntilChanged
} from 'rxjs';
import { pairwise } from 'rxjs/operators';
import { FEATURE_NAME } from './autoscaling-update-dialog.constant';
import { CommonModule } from '@angular/common';
import { ZuiMaterialStackModule } from '@zerops/zui/material-stack';
import { AutoscalingSettingsFormComponent } from '@zerops/zui/autoscaling-settings-form';
import { ZefProgressErrorModule } from '@zerops/zef/progress-error';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatIconModule } from '@angular/material/icon';
import { ServiceStackEntity, adjustServiceStackAutoscaling } from '@zerops/zerops/core/service-stack-base';
import { AppState } from '@zerops/zerops/app';
import { AdjustAutoscalingOutput } from '@zerops/zui/autoscaling-form-field/autoscaling-form-field.model';
import { ServiceStackCpuModes } from '@zerops/models/settings';
import { AutoscalingProfileOverridesValue, ServiceStack } from '@zerops/models/service-stack';
import { MergeStrategy, successOf } from '@zerops/zef/entities';
import { selectServiceStackTypesVersionsMap, loadStackTypeDetail } from '@zerops/zerops/core/settings-base';
import { UserEntity } from '@zerops/zerops/core/user-base';
import { round } from '@zerops/zef/utils';

@Component({
  selector: 'z-autoscaling-settings-dialog',
  templateUrl: './autoscaling-update-dialog.container.html',
  styleUrl: './autoscaling-update-dialog.container.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ZuiMaterialStackModule,
    ZefDialogModule,
    ZefScrollbarComponent,
    AutoscalingSettingsFormComponent,
    ZefProgressErrorModule,
    MatButtonModule,
    MatIconModule
  ]
})
export class AutoscalingSettingsDialogFeature extends ZefReactiveComponent {
  // # Deps
  #store = inject<Store<AppState>>(Store);
  #serviceStackEntity = inject(ServiceStackEntity);
  #userEntity = inject(UserEntity);
  #actions$ = inject(Actions);

  // # Event Streams
  onClose$ = new Subject<void>();
  onSubmit$ = new Subject<AdjustAutoscalingOutput>();

  // # Data
  // -- sync
  key = FEATURE_NAME;
  updateKey = adjustServiceStackAutoscaling.type;

  // -- async
  dialogState$ = this.#store.pipe(select(selectZefDialogState(FEATURE_NAME)));
  open$ = this.dialogState$.pipe(map((data) => data?.state));
  serviceStackId$ = this.dialogState$.pipe(map((data) => data?.meta?.serviceStackId));
  initialScalingType$ = this.dialogState$.pipe(map((data) => data?.meta?.initialScalingType));

  serviceStack$ = this.serviceStackId$.pipe(
    filter((id) => !!id),
    switchMap((id) => this.#serviceStackEntity.entityById$(id).pipe(filter((entity) => !!entity)))
  );

  autoscalingData$ = this.serviceStack$.pipe(
    map((serviceStack) => ({
      horizontalAutoscaling: serviceStack.customAutoscaling?.horizontalAutoscaling,
      verticalAutoscaling: {
        ...serviceStack.customAutoscaling?.verticalAutoscaling,
        minFreeResource: {
          ...serviceStack.customAutoscaling?.verticalAutoscaling?.minFreeResource,
          memoryGBytes: serviceStack.customAutoscaling?.verticalAutoscaling?.minFreeResource?.memoryGBytes
            ? round(serviceStack.customAutoscaling.verticalAutoscaling.minFreeResource.memoryGBytes, 3)
            : null,
          cpuCoreCount: serviceStack.customAutoscaling?.verticalAutoscaling?.minFreeResource?.cpuCoreCount
            ? round(serviceStack.customAutoscaling.verticalAutoscaling.minFreeResource.cpuCoreCount, 3)
            : null
        }
      },
      mode: serviceStack.mode,
      autoscalingProfileId: serviceStack.autoscalingProfileId,
      autoscalingProfileOverrides: serviceStack.autoscalingProfileOverrides ?? {}
    }))
  );

  // Internal form value for dialog editing - defined by the user
  autoscalingValue = signal<AdjustAutoscalingOutput | null>(null);

  // Validation check
  isFormValid = signal(true);

  // # Service stack type versions map for getting config
  serviceStackTypesVersionsMap$ = this.#store.pipe(select(selectServiceStackTypesVersionsMap));

  // # Service stack type config - like the original implementation
  serviceStackTypeConfig$ = this.serviceStack$.pipe(
    filter((d) => !!d),
    switchMap(({ serviceStackTypeVersionId }) => this.serviceStackTypesVersionsMap$.pipe(
      map((configs) => configs?.[serviceStackTypeVersionId]?.serviceStackTypeVersion?.config)
    ))
  );
  // Current effective CPU mode of the running service (for change warning)
  currentServiceCpuMode$ = this.serviceStack$.pipe(
    map((serviceStack) =>
      (serviceStack.currentAutoscaling?.verticalAutoscaling?.cpuMode
        ?? serviceStack.customAutoscaling?.verticalAutoscaling?.cpuMode) as ServiceStackCpuModes
    ),
    distinctUntilChanged()
  );

  // Free clients get the follow-up sentence about reapplying after limits increase;
  // verified accounts see the short explanation only.
  isFreeUser$ = this.#userEntity.activeClientUser$.pipe(
    map((clientUser) => clientUser?.client?.verifiedByTopUp === false),
    distinctUntilChanged()
  );

  onOpenDialog$ = this.open$.pipe(
    pairwise(),
    filter(([ prev, curr ]) => prev !== curr && !!curr),
    map(([ _, curr ]) => curr),
    withLatestFrom(this.autoscalingData$),
    tap(([ _, autoscalingData ]) => {
      if (autoscalingData) {
        this.autoscalingValue.set(autoscalingData);
      }
    })
  );

  // The entity store is populated from the /service-stack/search endpoint, which
  // doesn't return `autoscalingProfileOverrides` (only GET /service-stack/{id} does).
  // The detail page already issues a getOne with MergeStrategy.Noop — its response is
  // discarded by the reducer but the success action still carries the raw result, so we
  // re-issue getOne on dialog-open and read the override fields from the success payload.
  #onDialogOpenLoadServiceStack$ = this.open$.pipe(
    pairwise(),
    filter(([ prev, curr ]) => prev !== curr && !!curr),
    withLatestFrom(this.serviceStackId$),
    filter(([ , id ]) => !!id),
    map(([ , id ]) => this.#serviceStackEntity.getOne(id, {
      zefEntityMergeStrategy: MergeStrategy.Noop
    }))
  );

  #overridesFromBackend$ = this.#actions$.pipe(
    successOf<{ items?: ServiceStack[] }>(this.#serviceStackEntity.getOne),
    withLatestFrom(this.serviceStackId$),
    filter(([ action, id ]) => (action as any)?.originalAction?.data?.id === id),
    map(([ action ]) => {
      const raw = (action as any)?.meta?.rawResult as
        | { autoscalingProfileId?: string; autoscalingProfileOverrides?: AutoscalingProfileOverridesValue | null }
        | undefined;
      return {
        autoscalingProfileId: raw?.autoscalingProfileId,
        autoscalingProfileOverrides: raw?.autoscalingProfileOverrides ?? {}
      };
    })
  );

  // Patch the override fields into autoscalingValue when the GET response arrives.
  // Runs after onOpenDialog$ has set the initial value, so we merge non-destructively to
  // preserve any in-flight user edits to the scaling fields.
  #onOverridesArrived$ = this.#overridesFromBackend$.pipe(
    withLatestFrom(this.open$),
    filter(([ , open ]) => !!open),
    tap(([ overrides ]) => {
      this.autoscalingValue.update((current) => current ? {
        ...current,
        autoscalingProfileId: overrides.autoscalingProfileId ?? current.autoscalingProfileId,
        autoscalingProfileOverrides: overrides.autoscalingProfileOverrides
      } : current);
    })
  );


  // # State resolver
  state = this.$connect({
    open: this.open$,
    serviceStack: this.serviceStack$,
    autoscalingData: this.autoscalingData$,
    serviceStackTypeConfig: this.serviceStackTypeConfig$,
    serviceStackTypesVersionsMap: this.serviceStackTypesVersionsMap$,
    initialScalingType: this.initialScalingType$,
    currentServiceCpuMode: this.currentServiceCpuMode$,
    isFreeUser: this.isFreeUser$
  });

  // load stack type detail when dialog opens
  private _loadStackTypeDetailAction$ = this.serviceStack$.pipe(
    map((d) => d.serviceStackTypeId),
    distinctUntilChanged(),
    withLatestFrom(this.#userEntity.activeClientId$),
    map(([ stackTypeId, clientId ]) => loadStackTypeDetail({ clientId, stackTypeId }))
  );

  constructor() {
    super();

    // Init data set for dialog editing
    this.onOpenDialog$.pipe(takeUntilDestroyed()).subscribe();

    // Patch profile overrides into the form value once the GET response arrives.
    this.#onOverridesArrived$.pipe(takeUntilDestroyed()).subscribe();

    // # Dispatcher
    this.$dispatchActions([
      this._loadStackTypeDetailAction$,
      this.#onDialogOpenLoadServiceStack$,

      this.onClose$.pipe(
        map(() => zefDialogClose({ key: FEATURE_NAME }))
      ),

      this.onSubmit$.pipe(
        withLatestFrom(this.serviceStackId$),
        map(([ autoscalingData, serviceStackId ]) =>
          adjustServiceStackAutoscaling({
            serviceStackId,
            autoscalingProfileId: autoscalingData.autoscalingProfileId,
            autoscalingProfileOverrides: autoscalingData.autoscalingProfileOverrides
              && Object.keys(autoscalingData.autoscalingProfileOverrides).length
                ? autoscalingData.autoscalingProfileOverrides
                : null,
            customAutoscaling: {
              horizontalAutoscaling: autoscalingData.horizontalAutoscaling,
              verticalAutoscaling: autoscalingData.verticalAutoscaling
                ? {
                  ...autoscalingData.verticalAutoscaling,
                  cpuMode: autoscalingData.verticalAutoscaling?.cpuMode || undefined
                }
                : undefined
            }
          })
        )
      )
    ]);
  }
}
